import React, { useState } from "react";
import {
  useGetLoggedInUserOrderHistoryQuery,
  useCancelActiveOrderMutation,
  useReturnDeliveredOrderMutation,
} from "../../features/api/orderApiSlice";
import FuelLoading from "../../pages/FuelLoading";
import {
  FaUser,
  FaBox,
  FaHistory,
  FaShippingFast,
  FaCheckCircle,
  FaExclamationCircle,
  FaBan,
  FaUndoAlt,
} from "react-icons/fa";
import "./userDashboard.scss";
import { useGetUserProfileDetailsQuery } from "../../features/api/authApiSlice";
import { useSelector } from "react-redux";

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case "succeeded":
      return { text: "Paid & Packing", color: "#10b981", icon: <FaBox /> };
    case "shipped":
      return { text: "In Transit", color: "#3b82f6", icon: <FaShippingFast /> };
    case "delivered":
      return { text: "Delivered", color: "#f59e0b", icon: <FaCheckCircle /> };
    case "canceled":
      return { text: "Canceled", color: "#64748b", icon: <FaBan /> };
    case "returned":
      return { text: "Returned", color: "#ec4899", icon: <FaUndoAlt /> };
    default:
      return {
        text: "Processing",
        color: "#ef4444",
        icon: <FaExclamationCircle />,
      };
  }
};

export default function UserDashboard() {
  const [activeTab, setActiveTab] = useState("orders");

  // 🛡️ 1. Watch your active token status inside your central Auth state slice reducer
  const accessToken = useSelector((state) => state.auth?.accessToken);
  const isTokenLoaded = Boolean(accessToken);

  // 🛡️ 2. Connect hooks, but tell them to SKIP running if the token is not present yet!
  const {
    data: profileUser,
    isLoading: profileLoading,
    isError: profileError,
  } = useGetUserProfileDetailsQuery(undefined, { skip: !isTokenLoaded });
  const {
    data: myOrders = [],
    isLoading: ordersLoading,
    isError: ordersError,
  } = useGetLoggedInUserOrderHistoryQuery(undefined, { skip: !isTokenLoaded });

  // 🔌 Connect your cancel and return action hooks
  const [cancelOrder, { isLoading: isCanceling }] =
    useCancelActiveOrderMutation();
  const [returnOrder, { isLoading: isReturning }] =
    useReturnDeliveredOrderMutation();

  const handleCancelClick = async (orderId) => {
    if (
      window.confirm(
        "Are you sure you want to cancel this order? This will halt packaging processes immediately."
      )
    ) {
      try {
        await cancelOrder(orderId).unwrap();
        alert("🎉 Order successfully canceled.");
      } catch (err) {
        alert(err?.data?.message || "Failed to cancel order.");
      }
    }
  };

  const handleReturnClick = async (orderId) => {
    if (
      window.confirm(
        "Would you like to file a return request for this gear shipment?"
      )
    ) {
      try {
        await returnOrder(orderId).unwrap();
        alert("↩️ Return requested successfully.");
      } catch (err) {
        alert(err?.data?.message || "Failed to submit return request.");
      }
    }
  };

  // 🛡️ 3. Display a localized loading state while your app runs its token refresh cycle
  if (!isTokenLoaded || profileLoading || ordersLoading) {
    return (
      <div style={{ padding: "8rem 2rem" }}>
        <FuelLoading text="Syncing secure baseline encryption keys..." />
      </div>
    );
  }

  // 🛡️ 4. Now queries only throw an error if the authenticated endpoint genuinely fails
  if (profileError || ordersError) {
    return (
      <div
        style={{ color: "#ef4444", padding: "8rem 2rem", textAlign: "center" }}
      >
        ⚠️ Could not retrieve purchase parameters profiles logs. Please
        re-authenticate your session.
      </div>
    );
  }

  // 🔥 THE FIX: Safe deep extraction matching your raw backend payload (with NO hardcoded strings!)
  const targetUserObj = profileUser?.user || profileUser;
  const userName = targetUserObj?.name || "";
  const userEmail = targetUserObj?.email || "";
  const userAvatar = targetUserObj?.avatar || "";
  const userJoinDate = targetUserObj?.createdAt
    ? new Date(targetUserObj.createdAt).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
      })
    : "";

  return (
    <div className="user-dashboard-root fuel-fade-in">
      <div className="dashboard-container">
        {/* LEFT COMPONENT: STICKY NAVIGATION CONTROLS SIDEBAR */}
        <aside className="dashboard-sidebar">
          <div className="user-profile-summary">
            {/* Display profile avatar image asset link if available, otherwise display default icon vector */}
            {userAvatar ? (
              <img
                src={userAvatar}
                alt=""
                style={{
                  width: "64px",
                  height: "64px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  marginBottom: "12px",
                  border: "2px solid #f59e0b",
                }}
              />
            ) : (
              <div className="avatar-frame">
                <FaUser />
              </div>
            )}
            <h3>{userName}</h3>
            <span>{userEmail}</span>
          </div>

          <nav className="sidebar-nav">
            <button
              type="button"
              className={`nav-link ${activeTab === "orders" ? "active" : ""}`}
              onClick={() => setActiveTab("orders")}
            >
              <FaHistory /> Order History
            </button>
            <button
              type="button"
              className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => setActiveTab("profile")}
            >
              <FaUser /> Account Details
            </button>
          </nav>
        </aside>

        {/* RIGHT COMPONENT: INTERACTIVE CONTENT CARD TRUNK CONTAINER */}
        <main className="dashboard-main-content">
          {/* VIEW TAB A: ORDER HISTORY RECORDS LOG */}
          {activeTab === "orders" && (
            <section className="dashboard-view-section">
              <h2>📦 Your Order Tracker</h2>
              <p>
                Review the shipping routes, packed details, and status updates
                of your equipment acquisitions.
              </p>

              <div className="orders-timeline-stack">
                {myOrders.length === 0 ? (
                  <div className="empty-orders-fallback">
                    <h4>No Expeditions Logged Yet</h4>
                    <p>
                      Your basket history is clear. When you complete checkout
                      items, they will surface inside this hub grid track maps.
                    </p>
                  </div>
                ) : (
                  myOrders.map((order) => {
                    const badge = getStatusBadge(order.status);

                    const isReturnWindowValid =
                      (new Date() - new Date(order.createdAt)) /
                        (1000 * 60 * 60 * 24) <=
                      30;

                    return (
                      <div key={order._id} className="user-order-card">
                        <div className="order-card-header">
                          <div>
                            <strong>
                              ID: #
                              {order.stripePaymentIntentId
                                ?.slice(-8)
                                .toUpperCase()}
                            </strong>
                            <small>
                              Ordered:{" "}
                              {new Date(order.createdAt).toLocaleDateString()}
                            </small>
                          </div>

                          <span
                            className="status-indicator-tag"
                            style={{
                              border: `1px solid ${badge.color}`,
                              color: badge.color,
                            }}
                          >
                            {badge.icon} {badge.text}
                          </span>
                        </div>

                        <div className="order-card-grid">
                          <div className="items-list-tray">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="item-snapshot-row">
                                <img src={item.image} alt="" />

                                <div className="item-strings">
                                  <h5>{item.name}</h5>

                                  <div className="attributes-line">
                                    {item.size && (
                                      <span>
                                        Size: <strong>{item.size}</strong>
                                      </span>
                                    )}

                                    {(item.colorName || item.color) && (
                                      <span>
                                        Color:{" "}
                                        <strong>
                                          {item.colorName || item.color}
                                        </strong>
                                      </span>
                                    )}

                                    {item.volume && (
                                      <span>
                                        Vol: <strong>{item.volume}</strong>
                                      </span>
                                    )}
                                  </div>

                                  <span className="qty-indicator">
                                    Qty: {item.quantity} × $
                                    {Number(item.price || 0).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="order-summary-panel">
                            <div className="summary-row grand-total">
                              <span>Total Paid:</span>
                              <span>
                                $
                                {Number(order.financials?.total || 0).toFixed(
                                  2
                                )}
                              </span>
                            </div>

                            <div
                              className="lifecycle-actions-tray"
                              style={{
                                marginTop: "12px",
                                paddingTop: "10px",
                                borderTop: "1px solid rgba(255,255,255,0.06)",
                              }}
                            >
                              {order.status === "succeeded" && (
                                <button
                                  type="button"
                                  disabled={isCanceling}
                                  onClick={() => handleCancelClick(order._id)}
                                  className="action-row-btn action-row-btn--cancel"
                                  style={{
                                    width: "100%",
                                    padding: "6px",
                                    background: "rgba(239,68,68,0.1)",
                                    border: "1px solid #ef4444",
                                    color: "#ef4444",
                                    borderRadius: "6px",
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <FaBan />{" "}
                                  {isCanceling
                                    ? "Canceling Package..."
                                    : "Cancel Order Request"}
                                </button>
                              )}

                              {order.status === "delivered" &&
                                isReturnWindowValid && (
                                  <button
                                    type="button"
                                    disabled={isReturning}
                                    onClick={() => handleReturnClick(order._id)}
                                    className="action-row-btn action-row-btn--return"
                                    style={{
                                      width: "100%",
                                      padding: "6px",
                                      background: "rgba(245,158,11,0.1)",
                                      border: "1px solid #f59e0b",
                                      color: "#f59e0b",
                                      borderRadius: "6px",
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    <FaUndoAlt />{" "}
                                    {isReturning
                                      ? "Filing Return..."
                                      : "Request Gear Return (30 Days Limit)"}
                                  </button>
                                )}

                              {order.status === "delivered" &&
                                !isReturnWindowValid && (
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      color: "rgba(255,255,255,0.2)",
                                      display: "block",
                                      textAlign: "center",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    🔒 Return eligibility expired (Exceeded 30
                                    days)
                                  </span>
                                )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          )}

          {/* VIEW TAB B: ACCOUNT DETAILS PROFILE TRUNK CARD */}
          {activeTab === "profile" && (
            <section className="dashboard-view-section">
              <h2>👤 Member Settings Information</h2>
              <p>
                Manage your account configurations and profile credentials
                parameters safely.
              </p>

              <div
                className="profile-details-card-box"
                style={{
                  background: "#141414",
                  padding: "1.5rem",
                  borderRadius: "12px",
                  border: "1px solid rgba(255,255,255,0.04)",
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  marginTop: "1rem",
                }}
              >
                <div>
                  <small
                    style={{
                      color: "rgba(255,255,255,0.4)",
                      fontSize: "0.8rem",
                    }}
                  >
                    Display Name
                  </small>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "bold",
                      marginTop: "2px",
                    }}
                  >
                    {userName}
                  </div>
                </div>

                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid rgba(255,255,255,0.05)",
                  }}
                />

                <div>
                  <small
                    style={{
                      color: "rgba(255,255,255,0.4)",
                      fontSize: "0.8rem",
                    }}
                  >
                    Registered Contact Email
                  </small>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "bold",
                      marginTop: "2px",
                      color: "#f59e0b",
                    }}
                  >
                    {userEmail}
                  </div>
                </div>

                <hr
                  style={{
                    border: "none",
                    borderTop: "1px solid rgba(255,255,255,0.05)",
                  }}
                />

                <div>
                  <small
                    style={{
                      color: "rgba(255,255,255,0.4)",
                      fontSize: "0.8rem",
                    }}
                  >
                    Basecamp Member Since
                  </small>
                  <div
                    style={{
                      fontSize: "1rem",
                      fontWeight: "bold",
                      marginTop: "2px",
                    }}
                  >
                    {userJoinDate}
                  </div>
                </div>
              </div>
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
